/* eslint-disable */
import {
	faSend,
	faExpand,
	faSpinner,
	faCircleExclamation,
	faChevronDown,
	faChevronRight,
	faDownload,
} from '@fortawesome/pro-regular-svg-icons';
// the regular-weight stop glyph is a hollow outline — nearly invisible at
// 0.8rem inside the button. Stop is a filled square everywhere else.
import { faStop } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, TextInput, Modal, ModalVariant, TextArea, Tooltip } from '@patternfly/react-core';
import React, { useState, useEffect, useRef } from 'react';
import { ChatActions, ToolResultSummary, ToolSource } from '../../api/lambda/ChatActions';
import {
	ChatMessage,
	FilterItem,
	InovationChatRequest,
	InovationChatResponse,
	OrderByItem,
	Reasoning,
} from '../../api/types';
import { useApplication, useIsGranted } from '../user/ApplicationProvider';
import { Permission } from '../../enums/permission.enum';
import { getExtendedPrompt } from '../../api/zone/getExtendedPrompt';
import Markdown from './Markdown';
import MessageToolbar from './MessageToolbar';
import { Dataframe } from '../../api/dataframes/Dataframes';
import { useCommonStoreContext } from '../common-store/CommonStoreProvider';
import { useHighChart } from '../../hooks/HighChartHooks';
import { SavedChat } from '../../routes/discover/SavedChats';
import { getEnvironmentCode } from '../../helpers/general-helper-functions';
import { capitalizeFirstLetter } from '../../utilities';
import { platformLinkFor } from '../../helpers/platform-links';
import { stripInternalIds } from '../../helpers/strip-internal-ids';
import Highcharts from 'highcharts';

/**
 * ZAP-2532: a streamed bot message is an ordered list of segments — text runs
 * interleaved with inline tool-call citation cards.
 */
export type MessageSegment = { kind: 'text'; text: string } | { kind: 'tool'; callId: string };

/** ZAP-2532: the state of one tool call, rendered as an inline citation card. */
export interface ToolCallCite {
	name: string;
	step: string;
	/** the model's toolUseId (from the wire), when the backend sends one */
	id?: string;
	details?: Record<string, unknown>;
	status: 'running' | 'success' | 'error';
	summary?: ToolResultSummary;
	/** ZAP-2318: citation target for the "Open in ZeroedIn" link */
	source?: ToolSource;
}

export interface Message {
	id: number;
	text: string;
	isUser: boolean;
	sender: 'user' | 'bot';
	reasoning?: Reasoning[];
	chart?: any;
	aiChartSetup?: any;
	title?: any;
	/** ZAP-2461: true while this bot message is still receiving streamed tokens */
	isStreaming?: boolean;
	/** ZAP-2461: show a chart-skeleton placeholder while a chart tool is running */
	showChartSkeleton?: boolean;
	/** ZAP-2461: the user aborted the stream — renders a "(stopped)" suffix */
	stopped?: boolean;
	/** ZAP-2532: ordered text/tool segments (streamed messages only) */
	segments?: MessageSegment[];
	/** ZAP-2532: tool-call state keyed by segment callId */
	toolCalls?: Record<string, ToolCallCite>;
	/** ZAP-2579: when the message was sent, epoch ms; unknown for older saved rows */
	createdAt?: number;
}

/**
 * ZAP-2579: saved-chat rows carry an epoch timestamp written by the FastAPI
 * front door in milliseconds; tolerate seconds too, and treat 0/missing as
 * unknown so no time is shown rather than "56 years ago".
 */
export const toEpochMs = (ts: unknown): number | undefined => {
	if (typeof ts !== 'number' || !Number.isFinite(ts) || ts <= 0) return undefined;
	return ts < 1e12 ? ts * 1000 : ts;
};

/**
 * ZAP-2461: streaming consumption is on by default; flip localStorage key
 * 'zi.aiStreaming' to 'off' as a quick kill-switch (falls back to the
 * non-streaming request path).
 */
const STREAMING_ENABLED = true;
// ZAP-2582: how close to the bottom (px) counts as "at the bottom" when the
// user scrolls back down — the pin re-engages from here.
const SCROLL_PIN_THRESHOLD_PX = 100;

const isStreamingEnabled = (): boolean => {
	try {
		return STREAMING_ENABLED && localStorage.getItem('zi.aiStreaming') !== 'off';
	} catch (e) {
		return STREAMING_ENABLED;
	}
};

interface ChatInterfaceProps {
	context?: string;
	contextId?: string | number;
	externalThread?: boolean;
	onMessagesChange?: (messages: Message[]) => void;
	onThinkingChange?: (isThinking: boolean) => void;
	showModal?: boolean;
	onModalClose?: () => void;
	modalHeaderContent?: React.ReactNode;
	disabled?: boolean;
	onFirstInteraction?: () => void;
	autoSubmitMessage?: string;
	isSinglePage?: boolean;
	useInnovationAI?: boolean;
	onAutoSubmitted?: () => void;
	sessionId?: string;
	onSaveChat?: (sessionId: string, jobId: string, title: string) => void;
	canSaveChat?: boolean;
	maxPanelWidth?: number;
}

/**
 * ZAP-2352: builds the "Download as PNG" filename — a slugified chart title
 * (or a fallback) plus a `-YYYY-MM-DD` date suffix, capped around 80 chars,
 * with no extension (Highcharts appends `.png` itself).
 */
const slugifyChartFilename = (title?: string): string => {
	const slug = (title ?? '')
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');
	const base = slug.length > 0 ? slug : 'ai-analyst-chart';
	const dateSuffix = `-${new Date().toISOString().slice(0, 10)}`;
	const maxBaseLength = Math.max(1, 80 - dateSuffix.length);
	return `${base.slice(0, maxBaseLength)}${dateSuffix}`;
};

/** Renders chart messages with resizable container */
const ChartMessage: React.FC<{ chart: any; title?: any; text?: string; maxWidth?: number }> = ({
	chart,
	title,
	text,
	maxWidth,
}) => {
	const { options, setOptions, chartRender } = useHighChart(() => {}, false as boolean);

	useEffect(() => {
		if (chart) {
			setOptions(chart);
		}
	}, [chart, setOptions]);

	// ZAP-2532: fill the (now width-bounded) bubble by default; the resize
	// handle still lets a user drag it narrower.
	const [size, setSize] = useState({ width: '100%', height: 320 });
	const isResizing = useRef(false);
	const startPos = useRef({ x: 0, y: 0 });
	const startSize = useRef({ width: 0, height: 0 });
	const containerRef = useRef<HTMLDivElement>(null);

	// Reflow Highcharts when size changes using imported Highcharts directly
	useEffect(() => {
		const t = setTimeout(() => {
			Highcharts.charts.forEach((c) => {
				if (c && containerRef.current?.contains(c.container)) {
					c.reflow();
				}
			});
		}, 50);
		return () => clearTimeout(t);
	}, [size]);

	const onMouseDown = (e: React.MouseEvent) => {
		e.preventDefault();
		isResizing.current = true;
		startPos.current = { x: e.clientX, y: e.clientY };
		startSize.current = {
			width: containerRef.current?.offsetWidth ?? 0,
			height: containerRef.current?.offsetHeight ?? 0,
		};

		const onMouseMove = (e: MouseEvent) => {
			if (!isResizing.current || !containerRef.current) return;
			const newWidth = startSize.current.width + (e.clientX - startPos.current.x);
			const newHeight = startSize.current.height + (e.clientY - startPos.current.y);

			// Measure the enclosing bubble live on every drag tick — it's always
			// narrower than the panel (capped at 88% there — Layout.scss), so it
			// must win when both exist or a drag can grow the chart past the
			// bubble, which then gets clipped by `.chat-messages { overflow-x:
			// hidden }` and takes the resize handle down with it. Fall back to the
			// panel (e.g. inside the insights sidebars, which have no bubble) so a
			// drag can never grow the chart past its own container either.
			const bubble = containerRef.current.closest('.chat-bubble') as HTMLElement | null;
			const panel = containerRef.current.closest('.advanced-ai-body') as HTMLElement | null;
			const liveMax = bubble
				? bubble.clientWidth -
				  parseFloat(getComputedStyle(bubble).paddingLeft) -
				  parseFloat(getComputedStyle(bubble).paddingRight)
				: panel
				? panel.clientWidth -
				  parseFloat(getComputedStyle(panel).paddingLeft) -
				  parseFloat(getComputedStyle(panel).paddingRight)
				: maxWidth ?? 9999;

			setSize({
				width: `${Math.min(Math.max(200, newWidth), liveMax)}px`,
				height: Math.max(150, newHeight),
			});
		};

		const onMouseUp = () => {
			isResizing.current = false;
			window.removeEventListener('mousemove', onMouseMove);
			window.removeEventListener('mouseup', onMouseUp);
		};

		window.addEventListener('mousemove', onMouseMove);
		window.addEventListener('mouseup', onMouseUp);
	};

	// ZAP-2352: "Download as PNG" — Analyst-only, exports exactly what's on
	// screen right now (current size, labels, legend, colors) at 2x scale.
	const handleDownloadPng = () => {
		try {
			const chart = Highcharts.charts.find(
				(c) => c && containerRef.current?.contains(c.container)
			);
			if (!chart) return;

			const chartTitle =
				options?.title && typeof options.title === 'object'
					? (options.title as { text?: string }).text
					: undefined;
			const filename = slugifyChartFilename(chartTitle);

			chart.exportChartLocal(
				{
					type: 'image/png',
					filename,
					sourceWidth: chart.chartWidth,
					sourceHeight: chart.chartHeight,
					scale: 2,
				},
				{ exporting: { enabled: false, fallbackToExportServer: false } }
			);
		} catch (err) {
			console.error('AI Analyst chart PNG export failed', err);
		}
	};

	return (
		<div
			style={{
				width: size.width,
				maxWidth: maxWidth ? `${maxWidth}px` : '100%',
				overflow: 'hidden',
				transition: 'width 0.05s',
			}}
		>
			{text && text.length > 0 && (
				<div style={{ marginBottom: '8px' }}>
					<Markdown>{text}</Markdown>
					<br />
					<br />
				</div>
			)}
			<div
				ref={containerRef}
				style={{ position: 'relative', height: size.height, width: '100%' }}
			>
				{chartRender}
				{/* ZAP-2352: quiet download toolbar, top-right corner */}
				<div className="chat-chart-toolbar">
					<Tooltip content="Download as PNG">
						<Button
							variant="plain"
							aria-label="Download as PNG"
							className="chat-chart-toolbar-btn"
							onClick={handleDownloadPng}
						>
							<FontAwesomeIcon icon={faDownload} />
						</Button>
					</Tooltip>
				</div>
				{/* Resize handle — bottom-right corner */}
				<div
					onMouseDown={onMouseDown}
					style={{
						position: 'absolute',
						bottom: 0,
						right: 0,
						width: 16,
						height: 16,
						cursor: 'nwse-resize',
						background: 'linear-gradient(135deg, transparent 50%, #aaa 50%)',
						borderRadius: '0 0 4px 0',
					}}
				/>
			</div>
		</div>
	);
};

/**
 * ZAP-2462: elapsed timer + staged status copy shown next to the typing dots
 * while the AI is thinking. Mounts when isThinking flips true, so the start
 * timestamp is captured at that moment.
 */
const THINKING_STAGES: { threshold: number; copy: string }[] = [
	{ threshold: 90, copy: 'Still going — large requests can take a few minutes.' },
	{ threshold: 45, copy: 'Almost there — assembling the answer.' },
	{ threshold: 15, copy: 'Still working — complex queries can take a minute.' },
];

export const ThinkingStatus: React.FC = () => {
	const startRef = useRef(Date.now());
	const [elapsed, setElapsed] = useState(0);

	useEffect(() => {
		const id = setInterval(() => {
			setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
		}, 1000);
		return () => clearInterval(id);
	}, []);

	const stage = THINKING_STAGES.find((s) => elapsed >= s.threshold);
	const minutes = Math.floor(elapsed / 60);
	const seconds = String(elapsed % 60).padStart(2, '0');

	return (
		<div className="thinking-status">
			<span className="thinking-status-elapsed">
				{minutes}:{seconds}
			</span>
			{stage && <span className="thinking-status-copy">{stage.copy}</span>}
		</div>
	);
};

/**
 * ZAP-2461/ZAP-2532: key/value table for a reasoning step's (or tool call's)
 * request details. Shared by ReasoningSteps and ToolCallCard.
 */
const DetailsTable: React.FC<{ details: Record<string, unknown> }> = ({ details }) => (
	<table
		style={{
			marginTop: '6px',
			borderCollapse: 'collapse',
			fontSize: '14px',
			width: '100%',
		}}
	>
		<tbody>
			{Object.entries(details).map(([key, value]) => (
				<tr key={key}>
					<td
						style={{
							padding: '2px 8px 2px 0',
							fontWeight: 500,
							whiteSpace: 'nowrap',
							verticalAlign: 'top',
						}}
					>
						{capitalizeFirstLetter(key.replace(/_/g, ' '))}
					</td>
					<td
						style={{
							padding: '2px 0',
							verticalAlign: 'top',
							fontSize: '14px',
						}}
					>
						{Array.isArray(value)
							? (value as any[]).every((v) => typeof v === 'string')
								? (value as string[]).join(', ')
								: (value as (FilterItem | OrderByItem)[]).map((item, i) => (
										<span
											key={i}
											style={{
												display: 'block',
											}}
										>
											{Object.entries(item)
												.map(([k, v]) => `${k}: ${v}`)
												.join(' | ')}
										</span>
								  ))
							: String(value)}
					</td>
				</tr>
			))}
		</tbody>
	</table>
);

/**
 * ZAP-2461: shared reasoning-steps list — rendered collapsed inside <details>
 * for finished messages and open/inline above the growing text while streaming.
 */
const ReasoningSteps: React.FC<{ reasoning: Reasoning[] }> = ({ reasoning }) => (
	<ul>
		{reasoning.map((step, idx) => (
			<li key={idx}>
				<strong>{step.step}</strong>
				{step.details && Object.keys(step.details).length > 0 && (
					<DetailsTable details={step.details as Record<string, unknown>} />
				)}
			</li>
		))}
	</ul>
);

/** ZAP-2532: one-line plain-text rendering of a details value (collapsed row). */
const formatDetailValue = (value: unknown): string => {
	if (Array.isArray(value)) {
		if (value.every((v) => typeof v === 'string')) return value.join(', ');
		return value
			.map((item) =>
				item && typeof item === 'object'
					? Object.entries(item)
							.map(([k, v]) => `${k}: ${v}`)
							.join(' | ')
					: String(item)
			)
			.join('; ');
	}
	if (value && typeof value === 'object') {
		return Object.entries(value)
			.map(([k, v]) => `${k}: ${v}`)
			.join(' | ');
	}
	return String(value);
};

/** ZAP-2532: tool families, matched on the tool name. */
type ToolFamily = 'data' | 'chart' | 'document' | 'search' | 'generic';

const getToolFamily = (name: string): ToolFamily => {
	const n = (name || '').toLowerCase();
	if (n.startsWith('format_') || n.startsWith('render_chart') || n.includes('chart')) {
		return 'chart';
	}
	if (n.includes('data') || n.includes('query') || n.includes('sql') || n.includes('retrieve')) {
		return 'data';
	}
	if (
		n.includes('search') ||
		n.includes('list') ||
		n.includes('discover') ||
		n.includes('find')
	) {
		return 'search';
	}
	if (n.includes('doc') || n.includes('file') || n.includes('report') || n.includes('export')) {
		return 'document';
	}
	return 'generic';
};

/**
 * ZAP-2318: permission gating a tool-call citation's "Open in ZeroedIn" link —
 * a user who can't view the target page never sees a link to it.
 */
const SOURCE_PERMISSION: Record<ToolSource['type'], Permission> = {
	dataframe: Permission.ViewDataframe,
	dashboard: Permission.ViewDashboard,
	table: Permission.ViewTable,
	chart: Permission.ViewChart,
};

const formatBytes = (bytes: number): string => {
	if (!Number.isFinite(bytes) || bytes < 0) return '';
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * ZAP-2532: expanded result section of a tool-call card. Every summary key is
 * optional — renders whatever subset the backend supplied.
 */
const ToolResultSummaryView: React.FC<{ summary: ToolResultSummary }> = ({ summary }) => {
	const columns = Array.isArray(summary.columns)
		? summary.columns.map((c) => String(c))
		: undefined;
	const preview = Array.isArray(summary.preview)
		? summary.preview
				.filter((row): row is (string | number)[] => Array.isArray(row))
				.slice(0, 5)
		: undefined;
	const titles = Array.isArray(summary.titles) ? summary.titles.map((t) => String(t)) : undefined;
	const hasRows = typeof summary.rows === 'number';
	const hasCount = typeof summary.count === 'number';
	// ZAP-2532: bytes are dev-only detail — never shown as a line, just a tooltip on the label
	const bytesTitle = typeof summary.bytes === 'number' ? formatBytes(summary.bytes) : undefined;

	return (
		<div className="tool-call-detail-section">
			<div
				className="tool-call-detail-label"
				title={bytesTitle}
			>
				Result
			</div>
			{hasRows && (
				<div>
					{summary.rows} row{summary.rows === 1 ? '' : 's'}
					{columns ? ` × ${columns.length} column${columns.length === 1 ? '' : 's'}` : ''}
				</div>
			)}
			{preview && preview.length > 0 && (
				<div className="tool-call-preview-scroll">
					<table className="tool-call-preview-table">
						{columns && columns.length > 0 && (
							<thead>
								<tr>
									{columns.map((col, i) => (
										<th key={i}>{col}</th>
									))}
								</tr>
							</thead>
						)}
						<tbody>
							{preview.map((row, ri) => (
								<tr key={ri}>
									{row.map((cell, ci) => (
										<td key={ci}>{String(cell)}</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			{hasCount && (
				<div>
					{summary.count} result{summary.count === 1 ? '' : 's'}
				</div>
			)}
			{titles && titles.length > 0 && (
				<ul className="tool-call-titles">
					{titles.map((t, i) => (
						<li key={i}>{t}</li>
					))}
				</ul>
			)}
			{typeof summary.chart_type === 'string' && summary.chart_type.length > 0 && (
				<div>Chart type: {summary.chart_type}</div>
			)}
			{typeof summary.link === 'string' && summary.link.length > 0 && (
				<a
					className="tool-call-link"
					href={summary.link}
					target="_blank"
					rel="noopener noreferrer"
				>
					Open full result
				</a>
			)}
		</div>
	);
};

/** ZAP-2532: one line of the result summary shown under the request line. */
interface ResultLineInfo {
	text: string;
	/** 'preview' rows also toggle the expanded view; 'link' opens in a new tab. */
	kind: 'preview' | 'link' | 'text';
	href?: string;
}

/**
 * ZAP-2532: derives the quiet, single-line result summary shown as the third
 * line of a tool-call card (never the raw byte count — that's a tooltip in
 * the expanded view only). Returns null while there's nothing to summarize.
 */
const resultLine = (call: ToolCallCite): ResultLineInfo | null => {
	if (call.status === 'error') {
		const err = call.summary as (Record<string, unknown> & { message?: unknown }) | undefined;
		const message =
			(typeof err?.message === 'string' && err.message) ||
			(typeof err?.error === 'string' && (err.error as string)) ||
			'The tool call failed.';
		return { text: message, kind: 'text' };
	}

	const summary = call.summary;
	if (!summary) return null;
	const family = getToolFamily(call.name);

	if (family === 'chart') {
		if (typeof summary.link === 'string' && summary.link.length > 0) {
			return { text: 'Open image', kind: 'link', href: summary.link };
		}
		if (typeof summary.chart_type === 'string' && summary.chart_type.length > 0) {
			const chartType = summary.chart_type.toLowerCase();
			return {
				text: chartType.includes('chart') ? chartType : `${chartType} chart`,
				kind: 'text',
			};
		}
	}

	if (typeof summary.rows === 'number') {
		const columns = Array.isArray(summary.columns) ? summary.columns.length : undefined;
		const base = `${summary.rows.toLocaleString()} row${summary.rows === 1 ? '' : 's'}${
			columns ? ` × ${columns} column${columns === 1 ? '' : 's'}` : ''
		}`;
		// ZAP-2532 fix: only promise a preview when there's actually a non-empty
		// preview array to expand into — ToolResultSummaryView renders the table
		// solely when `summary.preview` has rows, so e.g. a 0-row result (or any
		// result whose backend omitted `preview`) must not offer a "preview ▸"
		// toggle that opens to nothing.
		const hasPreview = Array.isArray(summary.preview) && summary.preview.length > 0;
		return hasPreview
			? { text: `${base} · preview ▸`, kind: 'preview' }
			: { text: base, kind: 'text' };
	}

	if (typeof summary.count === 'number') {
		const titles = Array.isArray(summary.titles)
			? summary.titles.filter((t): t is string => typeof t === 'string')
			: [];
		const shown = titles.slice(0, 4).join(', ');
		const more = titles.length > 4 ? ', …' : '';
		return {
			text: `${summary.count} result${summary.count === 1 ? '' : 's'}${
				shown ? ` · ${shown}${more}` : ''
			}`,
			kind: 'text',
		};
	}

	if (typeof summary.link === 'string' && summary.link.length > 0) {
		return { text: 'Open image', kind: 'link', href: summary.link };
	}

	return null;
};

/**
 * ZAP-2532: inline tool-call citation card, rendered as three quiet lines —
 * a status dot + step label, a monospace request one-liner, and (once a
 * summary exists) a result line. Click the header row to expand the request
 * details table + result preview table.
 *
 * A chart-family card doubles as the chart skeleton while its tool is running
 * (replaces the separate ZAP-2461 placeholder). When the stream ends without a
 * tool_result (old backend, or user Stop), a still-"running" card degrades to
 * a muted completed state as soon as the message stops streaming.
 */
const ToolCallCard: React.FC<{ call?: ToolCallCite; live: boolean }> = ({ call, live }) => {
	const [expanded, setExpanded] = useState(false);
	const isGranted = useIsGranted();
	if (!call) return null;

	const family = getToolFamily(call.name);
	const running = call.status === 'running';
	const requestLine =
		call.details && Object.keys(call.details).length > 0
			? Object.entries(call.details)
					.map(
						([k, v]) =>
							`${capitalizeFirstLetter(k.replace(/_/g, ' '))}: ${formatDetailValue(
								v
							)}`
					)
					.join(' · ')
			: '';
	const hasDetails = Boolean(call.details && Object.keys(call.details).length > 0);
	const result = resultLine(call);
	const toggleExpanded = () => setExpanded((e) => !e);
	// ZAP-2532: a still-"running" card whose message stopped streaming (old
	// backend with no tool_result, or user Stop) degrades to a muted, settled
	// look rather than keeping the in-flight orange accent forever.
	const settled = running && !live;

	// ZAP-2318: "Open in ZeroedIn" citation link — only rendered when the
	// backend sent a source AND the viewer can actually open that page.
	const platformLink = call.source ? platformLinkFor(call.source) : undefined;
	const canOpenPlatformLink = Boolean(
		platformLink && call.source && isGranted(SOURCE_PERMISSION[call.source.type])
	);
	const platformLinkEl = canOpenPlatformLink ? (
		<a
			className="tool-call-platform-link"
			href={platformLink}
			target="_blank"
			rel="noopener noreferrer"
		>
			{`Open ${call.source!.type} ↗`}
		</a>
	) : null;

	return (
		<div
			className={`tool-call-card ${call.status}${settled ? ' settled' : ''}${
				expanded ? ' expanded' : ''
			}`}
		>
			<button
				type="button"
				className="tool-call-row"
				onClick={toggleExpanded}
				aria-expanded={expanded}
			>
				<span
					className={`tool-call-dot tool-call-dot-${call.status}${
						running && live ? ' pulse' : ''
					}`}
					aria-hidden="true"
				/>
				<span className="tool-call-step">{call.step || call.name}</span>
				{/* ZAP-2532: the dot alone carries the done state (green) — only an
				    in-flight spinner or an error mark earns a second glyph here */}
				{running && live ? (
					<span className="tool-call-status">
						<FontAwesomeIcon
							icon={faSpinner}
							spin
						/>
					</span>
				) : call.status === 'error' ? (
					<span className="tool-call-status">
						<FontAwesomeIcon
							icon={faCircleExclamation}
							className="tool-call-status-error"
						/>
					</span>
				) : null}
				<FontAwesomeIcon
					icon={expanded ? faChevronDown : faChevronRight}
					className="tool-call-chevron"
				/>
			</button>
			{requestLine && (
				<div
					className="tool-call-request-line"
					title={requestLine}
				>
					{requestLine}
				</div>
			)}
			{/* ZAP-2318: the result line is a flex row — the primary result content
			    plus, as a trailing item, the "Open in ZeroedIn" link. Neither is
			    nested inside the other (an <a> inside an <a>/<button> is invalid
			    and makes click targets unreliable — the same reason the platform
			    link lives outside the .tool-call-row toggle button). When there's
			    no result yet, the platform link renders alone on its own compact
			    line. */}
			{(result || platformLinkEl) && (
				<div className="tool-call-result-line">
					{result &&
						(result.kind === 'link' ? (
							<a
								className="tool-call-result-link"
								href={result.href}
								target="_blank"
								rel="noopener noreferrer"
							>
								{result.text}
							</a>
						) : result.kind === 'preview' ? (
							<button
								type="button"
								className="tool-call-result-toggle"
								onClick={toggleExpanded}
							>
								{result.text}
							</button>
						) : (
							<span
								className={
									call.status === 'error' ? 'tool-call-result-error' : undefined
								}
							>
								{result.text}
							</span>
						))}
					{platformLinkEl}
				</div>
			)}
			{/* ZAP-2532: the chart card itself is the skeleton while running */}
			{family === 'chart' && running && live && (
				<div
					className="chat-chart-skeleton tool-call-chart-skeleton"
					aria-label="Preparing chart..."
				/>
			)}
			{expanded && (
				<div className="tool-call-detail">
					{hasDetails && (
						<div className="tool-call-detail-section">
							<div className="tool-call-detail-label">Request</div>
							{/* ZAP-2532: the details table's key column is nowrap, so it
							    scrolls inside the card rather than widening the bubble */}
							<div className="tool-call-preview-scroll">
								<DetailsTable details={call.details as Record<string, unknown>} />
							</div>
						</div>
					)}
					{call.summary ? (
						<ToolResultSummaryView summary={call.summary} />
					) : (
						!hasDetails && (
							<div className="tool-call-empty">No additional detail available.</div>
						)
					)}
				</div>
			)}
		</div>
	);
};

export const ChatMessages: React.FC<{
	messages: Message[];
	isThinking: boolean;
	useInnovationAI?: boolean;
	maxWidth?: number;
}> = ({ messages, isThinking, useInnovationAI, maxWidth }) => {
	// ZAP-2573: the backend prompt asks the model not to surface internal ids
	// in prose, but streamed narration still sometimes leaks them — strip at
	// render, only on the AI Analyst path, never on the two insights
	// sidebars (which never pass useInnovationAI).
	const displayText = (text: string): string => (useInnovationAI ? stripInternalIds(text) : text);
	// ZAP-2579: what Copy puts on the clipboard — the markdown source of a
	// reply (text segments only; cards and charts are not text), the raw text
	// of a user message
	const copyText = (msg: Message): string => {
		if (msg.sender !== 'bot') return msg.text;
		const source = msg.segments?.length
			? msg.segments
					.filter((seg): seg is { kind: 'text'; text: string } => seg.kind === 'text')
					.map((seg) => seg.text)
					.join('\n')
					.trim()
			: msg.text;
		return displayText(source);
	};

	return (
		<>
			{messages.map((msg) => (
				<div
					key={msg.id}
					className={`chat-bubble ${msg.sender === 'user' ? 'user' : 'bot'}`}
					/* ZAP-2532: a chart bubble gets a definite width (100% of the message
				   list, same containing block as every other bot bubble) instead of
				   the old `width: fit-content`, whose percentage children resolved
				   against an undefined containing block and could grow past the
				   panel — clipping text and forcing a horizontal scrollbar. Only
				   `width` is set inline — `maxWidth` is left to the scoped 88% cap
				   (Layout.scss `.chat-container.innovation-ai .chat-bubble.bot`) so
				   the bubble doesn't jump to 100% and reflow the moment a chart
				   attaches at `done`. */
					style={msg.chart ? { width: '100%' } : undefined}
				>
					{/* ZAP-2461: while streaming, show reasoning steps open/inline above the
				    growing text — only for messages without segments (ZAP-2532 renders
				    inline tool cards instead) */}
					{msg.sender === 'bot' &&
						msg.isStreaming &&
						!msg.segments?.length &&
						msg.reasoning &&
						msg.reasoning.length > 0 && (
							<div className="chat-live-reasoning">
								<i>Reasoning</i>
								<ReasoningSteps reasoning={msg.reasoning} />
							</div>
						)}
					{msg.sender === 'bot' ? (
						msg.segments && msg.segments.length > 0 ? (
							/* ZAP-2532: segmented streamed message — text runs interleaved
						   with inline tool-call citation cards, in stream order */
							<>
								{msg.segments.map((seg, idx) =>
									seg.kind === 'text' ? (
										seg.text.trim().length > 0 ? (
											<Markdown key={`text-${idx}`}>
												{displayText(seg.text)}
											</Markdown>
										) : null
									) : (
										<ToolCallCard
											key={seg.callId}
											call={msg.toolCalls?.[seg.callId]}
											live={Boolean(msg.isStreaming)}
										/>
									)
								)}
								{msg.chart && (
									<ChartMessage
										chart={msg.chart}
										title={msg.title}
										maxWidth={maxWidth}
									/>
								)}
							</>
						) : msg.chart ? (
							<ChartMessage
								chart={msg.chart}
								title={msg.title}
								text={displayText(msg.text)}
								maxWidth={maxWidth}
							/>
						) : (
							<Markdown>{displayText(msg.text)}</Markdown>
						)
					) : (
						msg.text
					)}
					{/* ZAP-2461: chart-skeleton placeholder while a chart tool is running
				    (legacy — for segmented messages the chart tool card is the skeleton) */}
					{msg.sender === 'bot' && msg.showChartSkeleton && !msg.segments?.length && (
						<div
							className="chat-chart-skeleton"
							aria-label="Preparing chart..."
						/>
					)}
					{msg.sender === 'bot' && msg.stopped && (
						<span className="chat-stopped-suffix">(stopped)</span>
					)}
					{msg.sender === 'bot' &&
						!msg.isStreaming &&
						!msg.segments?.length &&
						msg.reasoning &&
						msg.reasoning.length > 0 && (
							<div className="chat-reasoning">
								<br />
								<details>
									<summary style={{ cursor: 'pointer' }}>
										<i>Reasoning</i>
									</summary>
									<ReasoningSteps reasoning={msg.reasoning} />
								</details>
							</div>
						)}
					{/* ZAP-2579: hover toolbar (Copy + relative time), Analyst page only,
					    once the message is complete */}
					{useInnovationAI && !msg.isStreaming && (
						<MessageToolbar
							text={copyText(msg)}
							createdAt={msg.createdAt}
						/>
					)}
				</div>
			))}
			{isThinking && (
				<div className="chat-bubble bot thinking">
					<div className="typing-indicator">
						<span className="dot"></span>
						<span className="dot"></span>
						<span className="dot"></span>
					</div>
					<ThinkingStatus />
				</div>
			)}
		</>
	);
};

/** Renders the pinned chat input area */
export const ChatInput: React.FC<{
	input: string;
	setInput: (val: string) => void;
	onSend: () => void;
	isThinking: boolean;
	placeholder?: string;
	disabled?: boolean;
	isModalOpen?: boolean;
	hideSendButton?: boolean;
	/** ZAP-2462: when set, shows a subtle "1 message queued" hint near the input */
	hasQueuedMessage?: boolean;
	/** ZAP-2461: while streaming, the send button becomes a stop button */
	isStreaming?: boolean;
	onStop?: () => void;
}> = ({
	input,
	setInput,
	onSend,
	isThinking,
	placeholder,
	disabled,
	isModalOpen,
	hideSendButton,
	hasQueuedMessage,
	isStreaming,
	onStop,
}) => {
	const measureRef = useRef<HTMLSpanElement | null>(null);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const textInputRef = useRef<any>(null);
	const textAreaRef = useRef<any>(null);
	const [isMultiline, setIsMultiline] = useState(false);

	useEffect(() => {
		if (!measureRef.current || !containerRef.current) return;

		const measure = () => {
			if (!measureRef.current || !containerRef.current) return;

			if (input.includes('\n')) {
				setIsMultiline(true);
				return;
			}

			measureRef.current.textContent = input || ' ';

			const textWidth = measureRef.current.scrollWidth;
			const containerWidth = containerRef.current.clientWidth;

			const ignore = containerWidth == 370 && isModalOpen;
			const offset = isModalOpen ? 183 : 100;

			if (!ignore) {
				setIsMultiline(textWidth + offset > containerWidth);
			}
		};

		requestAnimationFrame(measure);
	}, [input, isModalOpen]);

	useEffect(() => {
		setIsMultiline(false);
	}, [isModalOpen]);

	useEffect(() => {
		if (isModalOpen) {
			textAreaRef.current?.blur();
			textAreaRef.current?.focus?.();

			setTimeout(() => {
				textInputRef.current?.blur();
				textInputRef.current?.focus?.();
				const len = textInputRef.current.value.length;
				textInputRef.current.setSelectionRange(len, len);
			}, 50);
		}
	}, [isModalOpen]);

	useEffect(() => {
		if (!isMultiline) {
			requestAnimationFrame(() => {
				textInputRef.current?.focus?.();
			});
		} else {
			requestAnimationFrame(() => {
				textAreaRef.current?.focus?.();
				const len = textAreaRef.current.value.length;
				textAreaRef.current.setSelectionRange(len, len);
			});
		}
	}, [isMultiline]);

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			onSend();
		}
	};

	const handleKeyPressInput = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && e.shiftKey) {
			e.preventDefault();
			setIsMultiline(true);
			setInput(input + '\n');
			return;
		}
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			onSend();
		}
	};

	return (
		<div
			className="chat-input-group"
			ref={containerRef}
		>
			<span
				ref={measureRef}
				style={{
					position: 'absolute',
					visibility: 'hidden',
					whiteSpace: 'pre',
					fontSize: '14px',
					fontFamily: 'inherit',
					letterSpacing: 'inherit',
				}}
			/>

			{isMultiline ? (
				<TextArea
					ref={textAreaRef}
					value={input}
					onChange={setInput}
					onKeyDown={handleKeyPress}
					isDisabled={disabled}
					placeholder={placeholder ?? 'Type a message...'}
					autoResize
					rows={2}
					className={`taChatMessagePrompt ${isModalOpen ? 'chat-modal-open' : ''}`}
				/>
			) : (
				<TextInput
					ref={textInputRef}
					value={input}
					onChange={setInput}
					onKeyDown={handleKeyPressInput}
					isDisabled={disabled}
					placeholder={placeholder ?? 'Type a message...'}
				/>
			)}

			{!hideSendButton &&
				(isStreaming && onStop ? (
					<Button
						onClick={onStop}
						variant="primary"
						className="chat-send-btn chat-stop-btn"
						title="Stop generating"
					>
						<FontAwesomeIcon icon={faStop} />
					</Button>
				) : (
					<Button
						onClick={onSend}
						isDisabled={!input.trim() || disabled}
						variant="primary"
						className="chat-send-btn"
					>
						<FontAwesomeIcon icon={faSend} />
					</Button>
				))}

			{hasQueuedMessage && <div className="chat-queued-hint">1 message queued</div>}
		</div>
	);
};

const ChatInterface: React.FC<ChatInterfaceProps> = ({
	context = '',
	contextId,
	externalThread = false,
	onMessagesChange,
	onThinkingChange,
	showModal,
	onModalClose,
	modalHeaderContent,
	disabled,
	onFirstInteraction,
	autoSubmitMessage,
	isSinglePage,
	useInnovationAI,
	onAutoSubmitted,
	sessionId,
	onSaveChat,
	canSaveChat = false,
	maxPanelWidth,
}) => {
	const { zoneSettings, measures, user } = useApplication();
	const { setCurrentSelectedDataframe } = useCommonStoreContext();
	// This is the conversation the model is sent (see sendMessage's apiMessages).
	// A parent using externalThread renders its own copy for layout, but clearing
	// that copy does not reach this one — to genuinely start over, change the key.
	const [messages, setMessages] = useState<Message[]>([]);
	const [input, setInput] = useState('');
	const hasInteractedRef = useRef(false);
	const hasAutoSubmittedRef = useRef(false);
	const [isThinking, setIsThinking] = useState(false);
	const [internalShowModal, setInternalShowModal] = useState(false);
	// ZAP-2462: one pending message may be queued while a response is in flight;
	// sending again while one is queued replaces the queued text.
	const [queuedMessage, setQueuedMessage] = useState<string | null>(null);
	// ZAP-2461: true from stream start until done/error/abort. isThinking clears
	// at the first streamed token, so this is the flag that marks "a run is
	// still active" for queuing and for the stop affordance.
	const [isStreaming, setIsStreaming] = useState(false);
	const streamAbortRef = useRef<AbortController | null>(null);
	// ZAP-2461: distinguishes a discard-abort (unmount / session switch — the
	// stream's output must NOT touch the message list) from the user pressing
	// Stop (keep partial text and mark it "(stopped)").
	const streamDiscardedRef = useRef(false);

	// ZAP-2461: abort any in-flight stream on unmount so the stale closure
	// stops writing into state.
	useEffect(
		() => () => {
			streamDiscardedRef.current = true;
			streamAbortRef.current?.abort();
		},
		[]
	);

	const messagesEndRef = useRef<HTMLDivElement | null>(null);
	const chatContainerRef = useRef<HTMLDivElement | null>(null);
	const modalChatContainerRef = useRef<HTMLDivElement | null>(null);

	// Ref placed here (before any early returns) to comply with Rules of Hooks
	const chatPanelRef = useRef<HTMLDivElement>(null);
	const [chatPanelWidth, setChatPanelWidth] = useState<number>(0);

	const hideSend = Boolean(useInnovationAI && isSinglePage);

	const isModalOpen = showModal !== undefined ? showModal : internalShowModal;
	const handleModalClose = onModalClose ?? (() => setInternalShowModal(false));

	// Measure chat panel width and keep it updated on resize
	useEffect(() => {
		const measure = () => {
			if (chatPanelRef.current) {
				const style = window.getComputedStyle(chatPanelRef.current);
				const paddingLeft = parseFloat(style.paddingLeft);
				const paddingRight = parseFloat(style.paddingRight);
				setChatPanelWidth(chatPanelRef.current.clientWidth - paddingLeft - paddingRight);
			}
		};
		measure();
		const observer = new ResizeObserver(measure);
		if (chatPanelRef.current) observer.observe(chatPanelRef.current);
		return () => observer.disconnect();
	}, []);

	// ZAP-2582: the thread follows the stream only while it is pinned to the
	// bottom. Any upward scroll releases the pin; reaching the bottom again (by
	// hand or via the jump button) re-engages it. Refs, not state: the scroll
	// handler and the per-token effects read these constantly and must not
	// re-render the thread. showJumpToLatest mirrors !pinned for rendering.
	const pinnedToBottomRef = useRef(true);
	const showJumpToLatestRef = useRef(false);
	const lastScrollTopRef = useRef(0);
	const [showJumpToLatest, setShowJumpToLatest] = useState(false);

	const setPinnedToBottom = (pinned: boolean) => {
		pinnedToBottomRef.current = pinned;
		if (showJumpToLatestRef.current !== !pinned) {
			showJumpToLatestRef.current = !pinned;
			setShowJumpToLatest(!pinned);
		}
	};

	const getScrollContainer = () =>
		isModalOpen ? modalChatContainerRef.current : chatContainerRef.current;

	const scrollToBottom = (smooth = false) => {
		const container = getScrollContainer();
		if (container) {
			setPinnedToBottom(true);
			if (smooth && typeof container.scrollTo === 'function') {
				container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
			} else {
				container.scrollTop = container.scrollHeight;
			}
		}
	};

	// ZAP-2582: pin/release tracking on whichever container is live — the
	// modal has its own scroller, and in externalThread mode the inline one is
	// never attached (the sidebars own that thread and its pinning). Opening
	// or closing a view starts it pinned.
	useEffect(() => {
		setPinnedToBottom(true);
		const container = getScrollContainer();
		if (!container) return;
		lastScrollTopRef.current = container.scrollTop;

		const handleScroll = () => {
			const { scrollTop, scrollHeight, clientHeight } = container;
			const distance = scrollHeight - scrollTop - clientHeight;
			const movedUp = scrollTop < lastScrollTopRef.current;
			lastScrollTopRef.current = scrollTop;
			// Order matters. A content shrink (the thinking bubble going away)
			// clamps scrollTop, which reads as an upward move but lands at the
			// bottom — so "at the bottom" wins. Programmatic scrolls only ever
			// move down or land at the bottom, so they can never release the
			// pin; any upward movement is the user.
			if (distance < 2) {
				setPinnedToBottom(true);
			} else if (movedUp) {
				setPinnedToBottom(false);
			} else if (distance < SCROLL_PIN_THRESHOLD_PX) {
				setPinnedToBottom(true);
			}
		};

		// A wheel-up during a fast stream can be coalesced with the same
		// frame's programmatic scroll into one downward scroll event, hiding
		// the user's intent — so read the wheel directly as well.
		const handleWheel = (e: WheelEvent) => {
			// nothing to scroll — a wheel over a thread that fits is not a
			// request to stop following it
			if (e.deltaY < 0 && container.scrollHeight - container.clientHeight > 1) {
				setPinnedToBottom(false);
			}
		};

		container.addEventListener('scroll', handleScroll, { passive: true });
		container.addEventListener('wheel', handleWheel, { passive: true });
		return () => {
			container.removeEventListener('scroll', handleScroll);
			container.removeEventListener('wheel', handleWheel);
		};
	}, [isModalOpen]);

	// Reset chat when sessionId changes to a new blank session
	const prevSessionIdRef = useRef(sessionId);

	useEffect(() => {
		const prev = prevSessionIdRef.current;
		prevSessionIdRef.current = sessionId;

		if (prev === sessionId) return;

		// ZAP-2461: a stream belonging to the previous session must not keep
		// writing into the new one — discard-abort it and drop any queued
		// message before resetting.
		if (streamAbortRef.current) {
			streamDiscardedRef.current = true;
			streamAbortRef.current.abort();
		}
		setQueuedMessage(null);

		// Only reset for a genuinely new blank session (no contextId means not loading a saved chat)
		if (!contextId) {
			setMessages([]);
			setInput('');
			hasAutoSubmittedRef.current = false;
		}
	}, [sessionId]);

	useEffect(() => {
		onMessagesChange?.(messages);
	}, [messages]);

	useEffect(() => {
		onThinkingChange?.(isThinking);
	}, [isThinking]);

	useEffect(() => {
		if ((messages.length > 0 || isThinking) && pinnedToBottomRef.current) {
			scrollToBottom();
			// late growth — a chart mounting after its config arrives — lands
			// after this commit; re-check on the next frames, as the dashboard
			// sidebar does
			requestAnimationFrame(() =>
				requestAnimationFrame(() => {
					if (pinnedToBottomRef.current) scrollToBottom();
				})
			);
		}
	}, [messages, isThinking, isModalOpen]);

	const prevMessageCountRef = useRef(0);

	useEffect(() => {
		// ZAP-2461: skip streaming messages — while streaming, isThinking is
		// cleared explicitly when the first token arrives, not when the (empty)
		// bot message is created.
		if (
			messages.length > prevMessageCountRef.current &&
			messages[messages.length - 1]?.sender === 'bot' &&
			!messages[messages.length - 1]?.isStreaming &&
			isThinking
		) {
			const timer = setTimeout(() => {
				setIsThinking(false);
			}, 100);
			prevMessageCountRef.current = messages.length;
			return () => clearTimeout(timer);
		}
		prevMessageCountRef.current = messages.length;
	}, [messages]);

	const sanitizeHighchartsConfig = (config: any): any => {
		if (!config || typeof config !== 'object') return config;

		const functionKeys = [
			'formatter',
			'labelFormatter',
			'tooltipFormatter',
			'pointFormatter',
			'headerFormatter',
			'render',
			'callback',
			'complete',
			'step',
		];

		for (const key of Object.keys(config)) {
			if (functionKeys.includes(key) && typeof config[key] === 'string') {
				try {
					const fn = new Function('Highcharts', `return (${config[key]})`)(Highcharts);
					if (typeof fn === 'function') {
						config[key] = fn;
					} else {
						delete config[key];
					}
				} catch (e) {
					console.warn(`Removed unparseable formatter for "${key}":`, config[key]);
					delete config[key];
				}
			} else if (typeof config[key] === 'object' && config[key] !== null) {
				config[key] = sanitizeHighchartsConfig(config[key]);
			}
		}

		return config;
	};

	/**
	 * ZAP-2461: extracts a Highcharts config from the bot message text (or uses a
	 * directly supplied chart object), sanitizes it, attaches it to the message
	 * and selects a matching (or synthetic) dataframe. Shared by the streaming
	 * and non-streaming response paths.
	 *
	 * ZAP-2458: `directChart` is the backend's structured chart field (streaming
	 * `done.chart` / non-streaming `response.chart`). When present, the brace-scan
	 * is never used as the chart SOURCE (the structured field always wins); it
	 * remains as a one-release fallback for older backends that still embed the
	 * JSON in the response text. The scan still runs for text CLEANUP either way:
	 * during the transition the model may print the JSON into its text alongside
	 * the structured field, and the raw block must be stripped from the display.
	 * The ZAP-2532 segment stripping is a no-op when the text carries no JSON.
	 */
	const attachChartToMessage = async (msgId: number, fullText: string, directChart?: any) => {
		try {
			let chartObj: any = directChart ?? null;
			let textBeforeLocal: string | undefined = undefined;
			const extractJSON = (
				text: string
			): { parsed: any; start: number; end: number } | null => {
				for (let i = 0; i < text.length; i++) {
					if (text[i] !== '{') continue;
					let depth = 0;
					for (let j = i; j < text.length; j++) {
						if (text[j] === '{') depth++;
						else if (text[j] === '}') depth--;
						if (depth === 0) {
							const candidate = text.substring(i, j + 1);
							try {
								const parsed = JSON.parse(candidate);
								return { parsed, start: i, end: j + 1 };
							} catch (e) {
								break;
							}
						}
					}
				}
				return null;
			};
			// ZAP-2458: scan the text even when the chart came from the structured
			// field — during the transition the model may ALSO print the JSON into
			// its text (the old-backend-compat pre-prompt still asks it to), and
			// the raw block must be stripped from the displayed text either way.
			// With directChart present the scan only computes the stripped text;
			// the chart object itself always comes from the structured field.
			if (fullText && typeof fullText === 'string') {
				const parsedWithBounds = extractJSON(fullText);
				if (parsedWithBounds && parsedWithBounds.parsed && parsedWithBounds.parsed.chart) {
					if (!chartObj) {
						chartObj = parsedWithBounds.parsed;
					}
					const before = fullText.substring(0, parsedWithBounds.start).trim();
					const cleanedBefore = before.replace(/```(?:json)?\s*$/i, '').trim();
					textBeforeLocal = cleanedBefore;
				}
			}

			if (chartObj) {
				const safeChart = sanitizeHighchartsConfig(chartObj);
				// ZAP-2532: for segmented messages, strip the raw chart JSON from
				// the text segment that streamed it (the ChartMessage renders it
				// instead). Scans from the last segment back — the chart JSON is
				// the tail of the response.
				const stripChartFromSegments = (segments: MessageSegment[]): MessageSegment[] => {
					for (let i = segments.length - 1; i >= 0; i--) {
						const seg = segments[i];
						if (seg.kind !== 'text') continue;
						const bounds = extractJSON(seg.text);
						if (bounds && bounds.parsed && bounds.parsed.chart) {
							const before = seg.text
								.substring(0, bounds.start)
								.replace(/```(?:json)?\s*$/i, '')
								.trim();
							const next = [...segments];
							if (before.length > 0) {
								next[i] = { kind: 'text', text: before };
							} else {
								next.splice(i, 1);
							}
							return next;
						}
					}
					return segments;
				};
				setMessages((prev) =>
					prev.map((m) =>
						m.id === msgId
							? {
									...m,
									chart: safeChart,
									title: safeChart.title,
									text:
										textBeforeLocal && textBeforeLocal.length
											? textBeforeLocal
											: m.text,
									segments: m.segments
										? stripChartFromSegments(m.segments)
										: m.segments,
							  }
							: m
					)
				);

				let foundDf: any = null;
				try {
					const allFrames = await Dataframe.GetAll();
					if (measures && measures.length > 0) {
						for (const df of allFrames) {
							const dfNames = (df.datasets || []).map((d: any) =>
								(d.title || '').toString().toLowerCase()
							);
							const measureNames = measures.map((m: any) =>
								(m.name || m.title || m.id || '').toString().toLowerCase()
							);
							const matches = measureNames.every((mn: string) =>
								dfNames.includes(mn)
							);
							if (matches) {
								foundDf = df;
								break;
							}
						}
					}
				} catch (err) {
					// ignore
				}

				if (foundDf) {
					setCurrentSelectedDataframe(foundDf);
				} else {
					const fakeId = Date.now();
					const fakeDf: any = {
						id: fakeId,
						folder: 0,
						name: `AI chart - ${chartObj.title?.text ?? chartObj.title ?? 'Chart'}`,
						created_at: '',
						creator: 0,
						updated_at: '',
						updater: 0,
						columnEntry: [],
						datasets: (chartObj.series || []).map((s: any, idx: number) => ({
							id: fakeId + idx + 1,
							dataframe: fakeId,
							keyMeasureFact: 0,
							numDecimals: 2,
							title: s.name ?? `Series ${idx + 1}`,
						})),
						filters: [],
						order: [],
						rowEntry: [
							{
								id: fakeId + 1000,
								dataframe: fakeId,
								drillable: false,
								entity_id: 0,
								entity_type: 'Dimension',
								title: 'AI categories',
								totaled: false,
								use_sequence: false,
							},
						],
						type: 0,
						owner: 0,
						sharedDataframe: [],
					};
					setCurrentSelectedDataframe(fakeDf as any);
				}
			}
		} catch (err) {
			console.error('AI chart handling failed', err);
		}
	};

	/**
	 * ZAP-2461: sends the request via the SSE streaming endpoint, feeding tokens
	 * and reasoning steps into a live bot message. Returns true when the stream
	 * took ownership of the exchange (delivered content, was stopped by the
	 * user, or showed an error after tokens were already visible). Returns
	 * false when the caller should fall back to the non-streaming request.
	 */
	const sendViaStream = async (req: InovationChatRequest): Promise<boolean> => {
		const botId = Date.now() + 1;
		const controller = new AbortController();
		streamAbortRef.current = controller;
		streamDiscardedRef.current = false;

		let messageCreated = false;
		let tokensReceived = false;
		let doneReceived = false;
		let pendingText = '';
		// Full assistant text received so far (flushed + pending) — used as the
		// fallback final text when `done` carries no response.
		let accumulatedText = '';
		let flushTimer: ReturnType<typeof setTimeout> | null = null;
		let liveReasoning: Reasoning[] = [];
		// ZAP-2532: ordered text/tool segments + tool-call state, built live.
		let liveSegments: MessageSegment[] = [];
		let liveToolCalls: Record<string, ToolCallCite> = {};
		// Insertion order of tool callIds — used to match tool_result events to
		// the most recent running call.
		const toolCallOrder: string[] = [];
		let toolIndex = 0;
		// Cards whose completion came from a tool_result event. The backend
		// intentionally emits a `reasoning` frame right after each tool_result
		// (for older FE builds) — those reasoning frames must not create or
		// mutate cards, or every successful tool call would grow a duplicate.
		const resolvedByToolResult = new Set<string>();
		// Reverse-ordered lookup helper over toolCallOrder.
		const findCallId = (pred: (c: ToolCallCite) => boolean): string | undefined => {
			for (let i = toolCallOrder.length - 1; i >= 0; i--) {
				const id = toolCallOrder[i];
				const c = liveToolCalls[id];
				if (c && pred(c)) return id;
			}
			return undefined;
		};

		// True when the run was abandoned by a session switch or unmount: the
		// message list was replaced (or is going away), so this closure must not
		// write into it anymore.
		const isDiscarded = () => controller.signal.aborted && streamDiscardedRef.current;

		const updateBot = (patch: Partial<Message>) => {
			if (isDiscarded()) return;
			setMessages((prev) => prev.map((m) => (m.id === botId ? { ...m, ...patch } : m)));
		};

		// Create the bot message on the first piece of streamed content
		const ensureMessage = () => {
			if (messageCreated || isDiscarded()) return;
			messageCreated = true;
			setMessages((prev) => [
				...prev,
				{
					id: botId,
					createdAt: Date.now(),
					text: '',
					isUser: false,
					sender: 'bot',
					isStreaming: true,
				},
			]);
		};

		// ZAP-2532: append a text chunk to the trailing text segment (opening a
		// new one when the last segment is a tool card).
		const appendTextToSegments = (chunk: string) => {
			const last = liveSegments[liveSegments.length - 1];
			if (last && last.kind === 'text') {
				liveSegments = [
					...liveSegments.slice(0, -1),
					{ kind: 'text', text: last.text + chunk },
				];
			} else {
				liveSegments = [...liveSegments, { kind: 'text', text: chunk }];
			}
		};

		// Batch token appends (~50ms) to avoid render thrash. Flushes into both
		// the flat text blob and the trailing text segment (ZAP-2532).
		const flushTokens = () => {
			flushTimer = null;
			if (isDiscarded()) return;
			if (!pendingText) return;
			const chunk = pendingText;
			pendingText = '';
			appendTextToSegments(chunk);
			const segmentsSnapshot = [...liveSegments];
			setMessages((prev) =>
				prev.map((m) =>
					m.id === botId ? { ...m, text: m.text + chunk, segments: segmentsSnapshot } : m
				)
			);
		};

		const scheduleFlush = () => {
			if (flushTimer === null) {
				flushTimer = setTimeout(flushTokens, 50);
			}
		};

		const clearFlush = () => {
			if (flushTimer !== null) {
				clearTimeout(flushTimer);
				flushTimer = null;
			}
		};

		const normalizeReasoning = (steps: any[]): Reasoning[] =>
			steps.map((r: any) => (typeof r === 'string' ? { step: r, details: {} } : r));

		setIsStreaming(true);

		try {
			await ChatActions.sendInovationAIMessageStream(
				req,
				{
					onToken: ({ text: tokenText }) => {
						if (isDiscarded()) return;
						ensureMessage();
						if (!tokensReceived) {
							tokensReceived = true;
							// Dots vanish at first content
							setIsThinking(false);
						}
						pendingText += tokenText ?? '';
						accumulatedText += tokenText ?? '';
						scheduleFlush();
					},
					onToolStart: (evt) => {
						if (isDiscarded()) return;
						ensureMessage();
						liveReasoning = [
							...liveReasoning,
							{ step: evt.step ?? evt.name, details: (evt.details ?? {}) as any },
						];
						// ZAP-2532: flush queued tokens into the current trailing text
						// segment first, so text streamed before this event renders
						// before the card, then append the tool segment.
						clearFlush();
						flushTokens();
						const callId = `${evt.name ?? 'tool'}-${toolIndex++}`;
						liveToolCalls = {
							...liveToolCalls,
							[callId]: {
								name: evt.name ?? '',
								step: evt.step ?? evt.name ?? '',
								id: evt.id,
								details: evt.details,
								status: 'running',
								source: evt.source,
							},
						};
						toolCallOrder.push(callId);
						liveSegments = [...liveSegments, { kind: 'tool', callId }];
						updateBot({
							reasoning: liveReasoning,
							segments: [...liveSegments],
							toolCalls: liveToolCalls,
						});
					},
					// ZAP-2532: a tool call finished — attach the result summary.
					// Matched by toolUseId when the frame carries one; otherwise by
					// the most recent running call with the same name (fallback:
					// most recent running call of any name).
					onToolResult: (evt) => {
						if (isDiscarded()) return;
						ensureMessage();
						const status: ToolCallCite['status'] =
							evt.status === 'error' ? 'error' : 'success';
						let matchId: string | undefined;
						if (evt.id) {
							matchId = findCallId((c) => c.id === evt.id);
						}
						if (!matchId) {
							matchId = findCallId(
								(c) => c.status === 'running' && c.name === evt.name
							);
						}
						if (!matchId) {
							matchId = findCallId((c) => c.status === 'running');
						}
						if (matchId) {
							const existing = liveToolCalls[matchId];
							liveToolCalls = {
								...liveToolCalls,
								[matchId]: {
									...existing,
									status,
									id: evt.id ?? existing.id,
									summary: evt.summary ?? existing.summary,
									step: evt.step ?? existing.step,
									details: evt.details ?? existing.details,
									source: evt.source ?? existing.source,
								},
							};
							resolvedByToolResult.add(matchId);
							updateBot({ toolCalls: liveToolCalls });
						} else {
							// Orphan tool_result (no matching tool_start) — render a
							// pre-resolved card so the citation is still visible.
							clearFlush();
							flushTokens();
							const callId = `${evt.name ?? 'tool'}-${toolIndex++}`;
							liveToolCalls = {
								...liveToolCalls,
								[callId]: {
									name: evt.name ?? '',
									step: evt.step ?? evt.name ?? '',
									id: evt.id,
									details: evt.details,
									status,
									summary: evt.summary,
									source: evt.source,
								},
							};
							toolCallOrder.push(callId);
							resolvedByToolResult.add(callId);
							liveSegments = [...liveSegments, { kind: 'tool', callId }];
							updateBot({
								segments: [...liveSegments],
								toolCalls: liveToolCalls,
							});
						}
					},
					onReasoning: (evt) => {
						if (isDiscarded()) return;
						ensureMessage();
						const completed: Reasoning = {
							step: evt.step,
							details: (evt.details ?? {}) as any,
						};
						// Replace the matching in-progress (tool_start) step, else append
						const idx = liveReasoning.findIndex((r) => r.step === evt.step);
						if (idx >= 0) {
							liveReasoning = liveReasoning.map((r, i) =>
								i === idx ? completed : r
							);
						} else {
							liveReasoning = [...liveReasoning, completed];
						}
						// ZAP-2532: the new backend emits a `reasoning` frame right
						// AFTER each tool_result for the same call (kept for older
						// FE builds). A reasoning frame whose card was already
						// resolved by a tool_result is a pure echo — card-wise
						// no-op, or every successful call would grow a duplicate.
						//
						// By-id matching first (new backend); the name/step + LIFO
						// heuristics remain the old-backend fallback, where the
						// reasoning event IS the tool's completion signal.
						if (evt.id) {
							const byId = findCallId((c) => c.id === evt.id);
							if (byId) {
								if (
									resolvedByToolResult.has(byId) ||
									liveToolCalls[byId].status !== 'running'
								) {
									// Echo of an already-resolved call — cards untouched.
									updateBot({ reasoning: liveReasoning });
									return;
								}
								// tool_result never arrived for this id — the
								// reasoning frame resolves the card (plain success).
								const existing = liveToolCalls[byId];
								liveToolCalls = {
									...liveToolCalls,
									[byId]: {
										...existing,
										status: 'success',
										details: evt.details ?? existing.details,
									},
								};
								updateBot({
									reasoning: liveReasoning,
									toolCalls: liveToolCalls,
								});
								return;
							}
							// id present but no such card — fall through to the
							// heuristics below.
						}
						// Echo detection without ids: a card already resolved by a
						// tool_result with this label means this reasoning frame
						// belongs to it — skip.
						const echoOf = findCallId(
							(c) =>
								(c.step === evt.step || c.name === evt.step) &&
								c.status !== 'running'
						);
						if (echoOf && resolvedByToolResult.has(echoOf)) {
							updateBot({ reasoning: liveReasoning });
							return;
						}
						let resolvedId = findCallId(
							(c) =>
								c.status === 'running' &&
								(c.step === evt.step || c.name === evt.step)
						);
						// Bare LIFO fallback is old-backend-only: on the new backend
						// a running card's own tool_result is still coming — an
						// unrelated reasoning frame must not resolve it.
						if (!resolvedId && resolvedByToolResult.size === 0) {
							resolvedId = findCallId((c) => c.status === 'running');
						}
						if (resolvedId) {
							const existing = liveToolCalls[resolvedId];
							liveToolCalls = {
								...liveToolCalls,
								[resolvedId]: {
									...existing,
									status: 'success',
									details: evt.details ?? existing.details,
								},
							};
							updateBot({ reasoning: liveReasoning, toolCalls: liveToolCalls });
						} else if (resolvedByToolResult.size > 0) {
							// New backend (tool_result frames seen) and nothing left
							// running: this reasoning frame is an echo/afterthought —
							// never fabricate a card for it.
							updateBot({ reasoning: liveReasoning });
						} else {
							// Old backend only (no tool_result in this stream):
							// reasoning step with no running card — keep it visible
							// as a pre-completed card so the step isn't lost
							// (segments suppress the legacy live-reasoning list).
							clearFlush();
							flushTokens();
							const callId = `reasoning-${toolIndex++}`;
							liveToolCalls = {
								...liveToolCalls,
								[callId]: {
									name: evt.step ?? '',
									step: evt.step ?? '',
									details: evt.details,
									status: 'success',
								},
							};
							toolCallOrder.push(callId);
							liveSegments = [...liveSegments, { kind: 'tool', callId }];
							updateBot({
								reasoning: liveReasoning,
								segments: [...liveSegments],
								toolCalls: liveToolCalls,
							});
						}
					},
					onDone: (data) => {
						if (isDiscarded()) return;
						doneReceived = true;
						// ZAP-2532: fold any queued tokens into the trailing text
						// segment before finalizing (segments must equal the full
						// streamed text).
						clearFlush();
						if (pendingText) {
							appendTextToSegments(pendingText);
							pendingText = '';
						}
						ensureMessage();
						// done.response is authoritative for the final text; fall
						// back to the full accumulated token text otherwise.
						const resolvedText =
							typeof data.response === 'string' && data.response.length > 0
								? data.response
								: accumulatedText;
						// ZAP-2532 display choice: segments keep the streamed text.
						// When done.response is a pure extension of what streamed
						// (the only case where merging is trivially correct), the
						// tail is appended to the trailing text segment; otherwise
						// the streamed segments stand as the display and
						// done.response is used only for message.text + chart
						// extraction.
						if (
							liveSegments.length > 0 &&
							resolvedText.length > accumulatedText.length &&
							resolvedText.startsWith(accumulatedText)
						) {
							appendTextToSegments(resolvedText.slice(accumulatedText.length));
						}
						const finalReasoning = normalizeReasoning(
							(data.reasoning as any[]) ?? liveReasoning
						);
						// Segments only persist when tool cards exist — a pure-text
						// stream falls back to the flat text blob, which is the
						// authoritative done.response (exactly the ZAP-2461
						// behavior).
						const finalSegments =
							toolCallOrder.length > 0 ? [...liveSegments] : undefined;
						const finalToolCalls = toolCallOrder.length > 0 ? liveToolCalls : undefined;
						setMessages((prev) =>
							prev.map((m) =>
								m.id === botId
									? {
											...m,
											text: resolvedText,
											reasoning: finalReasoning,
											isStreaming: false,
											showChartSkeleton: false,
											segments: finalSegments,
											toolCalls: finalToolCalls,
									  }
									: m
							)
						);
						// ZAP-2458: prefer the structured chart field on done. When
						// present, attachChartToMessage never enters the brace-scan
						// (its extraction is guarded by !chartObj); the scan stays as
						// a one-release fallback for older backends whose done event
						// carries no chart field.
						void attachChartToMessage(botId, resolvedText, data.chart);
					},
					onError: () => {
						// Handled below: !doneReceived routes to the error/fallback path
					},
				},
				controller.signal
			);
		} catch (err) {
			// Failed before any event — silently fall back to non-streaming
			clearFlush();
			if (!controller.signal.aborted) {
				if (messageCreated) {
					// Only reasoning/steps may have been shown; remove the stub
					setMessages((prev) => prev.filter((m) => m.id !== botId));
				}
				setIsStreaming(false);
				if (streamAbortRef.current === controller) streamAbortRef.current = null;
				return false;
			}
		}

		clearFlush();
		// Only release the ref if this run still owns it — a discarded run must
		// not clobber a newer run's controller.
		if (streamAbortRef.current === controller) streamAbortRef.current = null;

		if (controller.signal.aborted) {
			const discarded = streamDiscardedRef.current;
			if (!discarded) {
				// User pressed Stop: keep the streamed text, mark it "(stopped)",
				// clear thinking, and do NOT auto-send a queued message.
				flushTokens();
				if (messageCreated) {
					updateBot({ isStreaming: false, showChartSkeleton: false, stopped: true });
				}
				setQueuedMessage(null);
			}
			// Discard-abort (session switch / unmount): the message list was
			// replaced (or the component is gone) — no message mutation at all.
			setIsThinking(false);
			setIsStreaming(false);
			return true;
		}

		if (doneReceived) {
			setIsThinking(false);
			setIsStreaming(false);
			return true;
		}

		// Stream errored or ended without `done`
		setIsStreaming(false);
		if (!tokensReceived) {
			// Nothing (or only reasoning) was shown — fall back silently
			if (messageCreated) {
				setMessages((prev) => prev.filter((m) => m.id !== botId));
			}
			return false;
		}

		// Tokens were already visible — replace with the standard error bubble text
		// (segments cleared so the error text is what renders — ZAP-2532)
		updateBot({
			text: 'Sorry, I encountered an error. Please try again.',
			isStreaming: false,
			showChartSkeleton: false,
			reasoning: undefined,
			segments: undefined,
			toolCalls: undefined,
		});
		setIsThinking(false);
		return true;
	};

	const sendMessage = async (text: string) => {
		if (!text.trim() || isThinking || isStreaming) return;

		const userMessage: Message = {
			id: Date.now(),
			createdAt: Date.now(),
			text,
			isUser: true,
			sender: 'user',
		};

		// ZAP-2582: sending always brings the thread back to the bottom
		setPinnedToBottom(true);
		setMessages((prev) => [...prev, userMessage]);
		setIsThinking(true);

		try {
			const apiMessages: ChatMessage[] = [
				...messages.map(
					(m): ChatMessage => ({
						role: m.sender === 'user' ? 'user' : 'assistant',
						content: m.text,
					})
				),
				{ role: 'user', content: text },
			];

			let content: string | undefined;
			let inovResp: InovationChatResponse = {
				response: '',
				session_id: '',
				status: 'pending',
				trace: [],
			};

			if (useInnovationAI) {
				let payloadText = text;
				try {
					const chartTrigger =
						/\b(?:create(?: me)?(?: a)?)?\s*(?:pie|bar|line|area|scatter|bubble|combo|waterfall|parallel\s*coord[a-z]*|spline|column|donut|funnel|heatmap|treemap)?\s*(?:chart|graph)\b/i;
					if (chartTrigger.test(text)) {
						payloadText = `${text}
						STRICT HIGHCHARTS RULES - follow exactly, no deviation:
						1. Only use built-in Highcharts core chart types: line, spline, area, areaspline, column, bar, pie, scatter, bubble, waterfall, heatmap, treemap, funnel, dependencywheel, sankey, solidgauge. DO NOT use any type that requires an additional Highcharts module (e.g. parallelCoordinates, networkgraph, timeline, variwide)
						2. NO dark backgrounds - chart.backgroundColor must always be "#ffffff", plotBackgroundColor must be null or omitted
						3. NO plotOptions.series.shadow, NO chart.style font overrides, NO custom CSS
						4. Use the zone color pallete for applicable chart colors if no zone colors configured use the defaults configured at the zone 
						5. legend.enabled = true, legend.layout = "horizontal", legend.align = "center", legend.verticalAlign = "bottom"
						6. credits.enabled = false
						7. title.text must be a concise descriptive label for the chart
						8. All axes must have a title.text label
						9. tooltip.shared = true where applicable
						10. JSON must be strictly valid — no trailing commas, no comments, no undefined values`;
					}
				} catch (e) {
					// ignore regex errors
				}

				const userIdentifer = user.account + getEnvironmentCode();

				const inovationReq: InovationChatRequest = {
					message: payloadText,
					session_id: sessionId,
					include_reasoning: true,
					userId: userIdentifer,
				};

				// ZAP-2461: try the SSE streaming endpoint first; fall back to the
				// non-streaming request when the stream is unusable.
				if (isStreamingEnabled()) {
					const handled = await sendViaStream(inovationReq);
					if (handled) return;
				}

				inovResp = await ChatActions.sendInovationAIMessage(inovationReq);
				content =
					inovResp?.response ?? (typeof inovResp === 'string' ? inovResp : undefined);
			} else {
				const resp = await ChatActions.SendMessage({
					messages: apiMessages,
					context: context,
					metadata: { extended_prompt: getExtendedPrompt(zoneSettings) },
				});

				content =
					resp?.data?.response?.content ??
					resp?.data?.response ??
					(resp && resp.data && typeof resp.data === 'string' ? resp.data : undefined);
			}

			if (content) {
				const aiMessage: Message = {
					id: Date.now() + 1,
					createdAt: Date.now(),
					text: content,
					isUser: false,
					sender: 'bot',
					reasoning: useInnovationAI
						? inovResp?.reasoning?.map((r: Reasoning) =>
								typeof r === 'string' ? { step: r, details: {} } : r
						  ) ?? []
						: undefined,
				};
				setMessages((prev) => [...prev, aiMessage]);

				// ZAP-2461: chart extraction shared with the streaming path
				await attachChartToMessage(
					aiMessage.id,
					typeof inovResp?.response === 'string' ? inovResp.response : '',
					inovResp?.chart
				);
			} else {
				const errorMessage: Message = {
					id: Date.now() + 1,
					createdAt: Date.now(),
					text: 'Sorry, I encountered an error. Please try again.',
					isUser: false,
					sender: 'bot',
				};
				setMessages((prev) => [...prev, errorMessage]);
			}
		} catch (error) {
			console.error('Send message error:', error);
			const errorMessage: Message = {
				id: Date.now() + 1,
				createdAt: Date.now(),
				text: 'Sorry, I encountered an error. Please try again.',
				isUser: false,
				sender: 'bot',
			};
			setMessages((prev) => [...prev, errorMessage]);
		}
	};

	const handleSend = async () => {
		if (!input.trim() || disabled) return;
		const text = input;
		setInput('');

		// ZAP-2462: while a response is in flight, queue exactly one pending
		// message (a second send replaces the queued text) instead of dropping it.
		// ZAP-2461: a streaming run stays "in flight" after isThinking clears at
		// the first token, so check isStreaming too.
		if (isThinking || isStreaming) {
			setQueuedMessage(text);
			return;
		}

		await sendMessage(text);
	};

	// ZAP-2462: auto-send the queued message once the current run completes.
	// ZAP-2461: with streaming, dispatch on done/error (isStreaming false), not
	// at the first token (which only clears isThinking).
	useEffect(() => {
		if (!isThinking && !isStreaming && queuedMessage) {
			const text = queuedMessage;
			setQueuedMessage(null);
			void sendMessage(text);
		}
	}, [isThinking, isStreaming, queuedMessage]);

	// ZAP-2461: abort the in-flight stream; the streamed text is kept and the
	// message is marked "(stopped)" by sendViaStream's abort handling.
	const handleStop = () => {
		streamAbortRef.current?.abort();
	};

	const doSend = () => void handleSend();

	useEffect(() => {
		const doAuto = async () => {
			if (
				autoSubmitMessage &&
				!hasAutoSubmittedRef.current &&
				!disabled &&
				!isThinking &&
				messages.length === 0
			) {
				hasAutoSubmittedRef.current = true;
				await sendMessage(autoSubmitMessage);
				onAutoSubmitted?.();
			}
		};
		void doAuto();
	}, [autoSubmitMessage, disabled, isThinking, messages.length, onAutoSubmitted]);

	const handleInputChange = (value: string) => {
		setInput(value);
		if (!hasInteractedRef.current && value.length > 0) {
			hasInteractedRef.current = true;
			onFirstInteraction?.();
		}
	};

	const containerClass = `chat-container ${useInnovationAI ? 'innovation-ai' : ''}`;

	// ZAP-2582: floating "jump to latest" — only while the thread is scrolled
	// away from the bottom. Rendered as a sibling of the scroller so it takes
	// no space in the message flow.
	const jumpToLatestButton = showJumpToLatest && messages.length > 0 && (
		<button
			type="button"
			className="chat-jump-latest"
			onClick={() => scrollToBottom(true)}
			aria-label="Jump to latest"
			title="Jump to latest"
		>
			<FontAwesomeIcon icon={faChevronDown} />
		</button>
	);

	// Must come after all hooks, before any conditional returns
	// Resolve effective max width: prefer the prop passed from AdvancedAI,
	// fall back to the locally measured chat panel width
	const effectiveMaxWidth = maxPanelWidth || chatPanelWidth || undefined;

	if (externalThread) {
		return (
			<>
				<ChatInput
					input={input}
					setInput={handleInputChange}
					onSend={doSend}
					isThinking={isThinking}
					disabled={disabled}
					isModalOpen={isModalOpen}
					hideSendButton={hideSend}
					hasQueuedMessage={queuedMessage !== null}
					isStreaming={isStreaming}
					onStop={handleStop}
				/>
				<Modal
					variant={ModalVariant.large}
					title="AI * Insights"
					isOpen={isModalOpen}
					onClose={handleModalClose}
					className="chat-modal"
				>
					<div className={`${containerClass} chat-container-modal`}>
						<div
							className="chat-messages chat-messages-modal"
							ref={modalChatContainerRef}
						>
							{modalHeaderContent}
							<ChatMessages
								messages={messages}
								isThinking={isThinking}
							/>
							<div ref={messagesEndRef} />
						</div>
						{jumpToLatestButton}
						<ChatInput
							input={input}
							setInput={setInput}
							onSend={doSend}
							isThinking={isThinking}
							isModalOpen={isModalOpen}
							hasQueuedMessage={queuedMessage !== null}
							isStreaming={isStreaming}
							onStop={handleStop}
						/>
					</div>
				</Modal>
			</>
		);
	}

	// chatContent helper — isModal param controls which refs/classes to use
	const chatContent = (isModal: boolean) => (
		<>
			<div
				className={`chat-messages ${isModal ? 'chat-messages-modal' : ''}`}
				ref={isModal ? modalChatContainerRef : chatContainerRef}
			>
				<ChatMessages
					messages={messages}
					isThinking={isThinking}
					useInnovationAI={useInnovationAI}
					maxWidth={effectiveMaxWidth}
				/>
				<div ref={messagesEndRef} />
			</div>
			{jumpToLatestButton}

			<div className="chat-input-group">
				<ChatInput
					input={input}
					setInput={setInput}
					onSend={doSend}
					isThinking={isThinking}
					disabled={disabled}
					isModalOpen={isModal}
					hasQueuedMessage={queuedMessage !== null}
					isStreaming={isStreaming}
					onStop={handleStop}
				/>
			</div>
		</>
	);

	// Load saved conversation when sessionId changes
	useEffect(() => {
		const loadSavedConversation = async () => {
			if (sessionId) {
				try {
					const response = await ChatActions.getInovationAIChatHistory(
						sessionId,
						String(contextId || '')
					);

					if (response && response.messages && response.messages.length > 0) {
						setIsThinking(true);

						let loadedMessages: Message[] = response.messages.map(
							(msg: any, idx: number) => ({
								id: idx,
								createdAt: toEpochMs(msg.timestamp),
								text: msg.content || msg.text,
								isUser: msg.role === 'user',
								sender: msg.role === 'user' ? 'user' : 'bot',
								reasoning: msg.reasoning || undefined,
								// ZAP-2458: the backend now stores the structured chart with the
								// message; treat it like the live `done.chart` so a reopened chat
								// renders the same chart it streamed.
								chart: msg.chart ? sanitizeHighchartsConfig(msg.chart) : undefined,
								title: msg.chart ? msg.chart.title : undefined,
							})
						);

						const chartInstructionPattern = /\s*STRICT HIGHCHARTS RULES[\s\S]*/g;

						loadedMessages = loadedMessages.map((msg) => ({
							...msg,
							text: msg.isUser
								? msg.text.replace(chartInstructionPattern, '').trim()
								: msg.text,
						}));

						// Extract and sanitize charts from bot messages
						loadedMessages = loadedMessages.map((msg) => {
							if (!msg.isUser && !msg.chart && msg.text) {
								const extractJSON = (
									text: string
								): { parsed: any; start: number; end: number } | null => {
									for (let i = 0; i < text.length; i++) {
										if (text[i] !== '{') continue;
										let depth = 0;
										for (let j = i; j < text.length; j++) {
											if (text[j] === '{') depth++;
											else if (text[j] === '}') depth--;
											if (depth === 0) {
												const candidate = text.substring(i, j + 1);
												try {
													const parsed = JSON.parse(candidate);
													return { parsed, start: i, end: j + 1 };
												} catch (e) {
													break;
												}
											}
										}
									}
									return null;
								};

								const parsedWithBounds = extractJSON(msg.text);
								if (
									parsedWithBounds &&
									parsedWithBounds.parsed &&
									parsedWithBounds.parsed.chart
								) {
									const before = msg.text
										.substring(0, parsedWithBounds.start)
										.trim();
									const cleanedBefore = before
										.replace(/```(?:json)?\s*$/i, '')
										.trim();

									return {
										...msg,
										chart: sanitizeHighchartsConfig(parsedWithBounds.parsed),
										title: parsedWithBounds.parsed.title,
										text: cleanedBefore || msg.text,
									};
								}
							}
							return msg;
						});

						// ZAP-2573: strip leaked internal ids from replayed bot prose too —
						// this loader is Innovation-AI-only by construction (it calls the
						// AI Analyst /history endpoint), and this text is terminal display
						// text for the resumed session (same as the STRICT HIGHCHARTS RULES
						// strip on user messages and the chart-JSON strip above), not the
						// live streamed text other logic still needs raw.
						loadedMessages = loadedMessages.map((msg) =>
							msg.isUser ? msg : { ...msg, text: stripInternalIds(msg.text) }
						);

						setMessages(loadedMessages);
						setTimeout(() => scrollToBottom(), 100);
						setIsThinking(false);
					} else {
						// Don't wipe if auto-submit is responsible for populating messages
						if (!hasAutoSubmittedRef.current && !autoSubmitMessage) {
							setMessages([]);
							setInput('');
						}

						if (!autoSubmitMessage) {
							setIsThinking(false);
						}
					}
				} catch (error) {
					console.error('Error loading saved conversation:', error);
					if (!autoSubmitMessage) {
						setIsThinking(false);
					}
				}
			}
		};

		loadSavedConversation();
	}, [sessionId, autoSubmitMessage]);

	// Save chat metadata after each bot response
	const saveChat = (msgs: Message[]) => {
		if (!canSaveChat || msgs.length < 2) return;

		const currentSessionId = sessionId || String(Date.now());
		const currentJobId = String(contextId);
		const firstUserMsg = msgs.find((m) => m.isUser)?.text || 'Untitled';
		const title = firstUserMsg.substring(0, 50) + (firstUserMsg.length > 50 ? '...' : '');

		const chatData: SavedChat = {
			session_id: currentSessionId,
			job_id: currentJobId,
			title,
			created_at: new Date().toISOString(),
			last_activity: new Date().toISOString(),
			numberOfMsgs: msgs.length,
		};

		onSaveChat?.(currentSessionId, currentJobId, title);
	};

	useEffect(() => {
		if (canSaveChat && messages.length > 1) {
			saveChat(messages);
		}
	}, [messages, canSaveChat, sessionId, contextId]);

	return (
		<>
			{/* chatPanelRef attached here — measures the true inner container width */}
			<div
				className={containerClass}
				ref={chatPanelRef}
			>
				{!isSinglePage && (
					<div className="chat-header">
						<small>
							<FontAwesomeIcon icon={faExpand} /> Follow up question? Prompt to get
							further insight...
						</small>
						<Button
							variant="plain"
							className="chat-expand-btn"
							onClick={() => setInternalShowModal(true)}
							title="Expand chat"
						>
							<FontAwesomeIcon icon={faExpand} />
						</Button>
					</div>
				)}

				{chatContent(false)}
			</div>

			<Modal
				variant={ModalVariant.large}
				title="AI Chat"
				isOpen={internalShowModal}
				onClose={() => setInternalShowModal(false)}
				className="chat-modal"
			>
				<div className="chat-container chat-container-modal">{chatContent(true)}</div>
			</Modal>
		</>
	);
};

export default ChatInterface;
